import { Formik, Form } from 'formik'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import styled from 'styled-components'
import { useEventSlug } from 'hooks'
import { _, withCapture } from 'utils'
import { BoothCategoryType, BoothTypeOption } from 'generated/graphql'
import Button from 'components/Button'
import { GroupedSelectField } from 'components/ReusableStyledComponents'
import SubmitButton from 'features/forms/components/SubmitButton'
import NumberField from 'features/forms/components/NumberField'
import SelectField from 'features/forms/components/SelectField'
import TextField from 'features/forms/components/TextField'
import { showNotification } from 'features/notifications'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { convertGroupedOptions } from 'features/shows/tabs/App'
import {
    createBooth,
    updateBooth,
    inviteAdmin,
    removeAdmin
} from './graphql/mutation'
import Switch from '../forms/components/Switch'
import { Booth } from './BoothList'
import { getEventData, mapsSelector } from '../events/eventSlice'
import InviteEmailsField from '../forms/components/invite_email/InviteEmailsField'

const S = {
    Switch: styled(Switch)`
        margin-top: 5px;
        margin-bottom: 40px !important;
    `,
    Actions: styled.div`
        display: flex;
        justify-content: flex-end;
        width: 100%;
        padding-right: 10px;
    `
}

interface Props {
    close?: () => void
    callGetBooths?: () => void
    minOrder?: number
    action: 'create' | 'update'
    booth?: Booth
    categories?: BoothCategoryType[]
}

const createdInitialValues = {
    parent: {
        name: '',
    },
    inviteStaff: '',
    type: BoothTypeOption.Medium,
    maxDocuments: 0,
    maxStaff: 0,
    maxVideos: 0,
    maxJobOpenings: 0,
}

const BoothValidation = (isCreating) => Yup.object().shape({
    parent: Yup.object().shape({
        name: Yup.string().required().min(3),
    }),
    inviteStaff: isCreating ? Yup.string().email().required() : Yup.string().email(),
    maxDocuments: Yup.number().integer().min(0),
    maxStaff: Yup.number().integer().min(0),
    maxVideos: Yup.number().integer().min(0),
    maxJobOpenings: Yup.number().integer().min(0),
})

const CreateUpdateBooth = ({
    close, callGetBooths, minOrder = 0, action, booth, categories
}: Props) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const maps = useSelector(mapsSelector)
    const isCreating = action === 'create'
    const updatedInitialValues = {
        parent: {
            name: booth?.parent?.name
        },
        type: booth?.type,
        maxStaff: booth?.maxStaff,
        maxDocuments: booth?.maxDocuments,
        maxVideos: booth?.maxVideos,
        enableChat: booth?.enableChat,
        enableVideocall: booth?.enableVideocall,
        staffMembers: booth?.staffMembers,
        maxJobOpenings: booth?.maxJobOpenings,
        physicalLocationId: booth?.physicalLocation?.id,
        enableVisitorList: booth?.enableVisitorList,
        categories: booth?.categories.map((e) => e.id),
    }

    const onSubmit = withCapture(async (values, { setFieldError }) => {
        delete values.staffMembers
        const response = isCreating
            ? await createBooth({
                eventId: eventSlug,
                data: { ...values, order: minOrder-1000 },
            })
            : await updateBooth(
                {
                    id: booth.id,
                    data: values,
                }
            )
        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: isCreating
                    ? _('form.notification.create_success')
                    : _('form.notification.update_success'),
            }))
            callGetBooths()
            isCreating && dispatch(getEventData(eventSlug))
            close()
        } else {
            const messages = response.errors
            // eslint-disable-next-line max-len
            const duplicateSlugError = 'duplicate key value violates unique constraint "virtual_space_slug'
            if (messages.find((e) => e.includes(duplicateSlugError))) {
                setFieldError('parent.name', _('booths.create_booth.duplicate_name'))
            } else {
                dispatch(showErrorNotification(messages))
            }
        }
    })

    return (
        <Formik
            initialValues={isCreating ? createdInitialValues : updatedInitialValues}
            validationSchema={BoothValidation(isCreating)}
            onSubmit={onSubmit}
        >
            {({ values }) => (
                <Form className="row">
                    <TextField
                        name="parent.name"
                        label={_('booths.form.company_name')}
                        placeholder={_('booths.form.company_name_placeholder')}
                        isRequired
                    />
                    <SelectField
                        name="type"
                        label={_('booths.form.type')}
                        options={[
                            { label: BoothTypeOption.Large, value: BoothTypeOption.Large },
                            { label: BoothTypeOption.Medium, value: BoothTypeOption.Medium },
                            { label: BoothTypeOption.Small, value: BoothTypeOption.Small },
                        ]}
                        lg={1/2}
                        isClearable={false}
                        helperText={_('booths.form.booth_type_helper_text')}
                    />
                    <GroupedSelectField
                        name="physicalLocationId"
                        label={_('booths.form.physical_location')}
                        helperText={_('booths.form.physical_location.helper_text')}
                        options={convertGroupedOptions(maps)}
                        optionLabel="name"
                        optionValue="id"
                        lg={1 / 2}
                        showInfoIcon
                    />
                    {
                        isCreating ? (
                            <TextField
                                name="inviteStaff"
                                label={_('booths.form.booth_admin')}
                                placeholder={_('booths.form.booth_admin_placeholder')}
                                isRequired={isCreating}
                                lg={1/2}
                            />
                        ) : (
                            // We cant render InviteEmailsField when creating,
                            // because booth.id is undefined when calling
                            // "inviteAdmin(booth.id, email)"
                            <InviteEmailsField
                                name="staffMembers"
                                label={_('booths.form.booth_admin')}
                                placeholder={_('booths.form.booth_admin_placeholder')}
                                isRequired={isCreating}
                                helperText={_('booths.form.booth_admin_helper_text')}
                                modalSize="sm"
                                onInvite={(email) => inviteAdmin(booth.id, email)}
                                onRemove={(email) => removeAdmin(booth.id, email)}
                                callback={callGetBooths}
                                staffsInfo={{ total: values?.staffMembers?.length, max: values.maxStaff }}
                            />
                        )
                    }
                    <SelectField
                        name="categories"
                        label={_('booth.config.general_info.categories')}
                        optionValue="id"
                        optionLabel="name"
                        options={categories}
                        isMulti
                    />
                    <NumberField
                        name="maxStaff"
                        label={_('booths.form.max_staffs')}
                        helperText={_('booths.form.max_staffs_helper_text')}
                        lg={1/2}
                    />
                    <NumberField
                        name="maxJobOpenings"
                        label={_('booths.form.max_jobs')}
                        helperText={_('booths.form.max_jobs_helper_text')}
                        lg={1/2}
                    />
                    <div style={{ width: '100%' }} />
                    <NumberField
                        name="maxDocuments"
                        label={_('booths.form.max_documents')}
                        helperText={_('booths.form.max_documents_helper_text')}
                        lg={1/2}
                    />
                    <NumberField
                        name="maxVideos"
                        label={_('booths.form.max_videos')}
                        helperText={_('booths.form.max_videos_helper_text')}
                        lg={1/2}
                    />
                    <S.Switch
                        name="enableChat"
                        label={_('booths.form.enable_chat')}
                        helperText={_('booths.form.enable_chat_helper_text')}
                        lg={1/2}
                    />
                    <S.Switch
                        name="enableVideocall"
                        label={_('booths.form.enable_videocall')}
                        lg={1/2}
                        helperText={_('booths.form.enable_videocall_helper_text')}
                    />
                    <S.Switch
                        name="enableVisitorList"
                        label={_('booths.form.enable_visitor_list')}
                        lg={1/2}
                        helperText={_('booths.form.enable_visitor_list_helper_text')}
                    />
                    <S.Actions>
                        <SubmitButton />
                        <Button variant="secondary" onClick={close}>{_('button.cancel')}</Button>
                    </S.Actions>
                </Form>
            )}
        </Formik>
    )
}

export default CreateUpdateBooth
